import { useDecision } from '@optimizely/react-sdk';
import { useEffect, useState, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useHistory } from 'react-router-dom';

import { backendAnalyticsTrack } from 'analytics/backend';
import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { InitialValues } from 'forms/types';
import useNavigatorTracking from 'hooks/useNavigatorTracking';
import Steps from 'hooks/useNavigatorTracking/steps';
import { OCCUPATION_PATH, HEALTH_BASICS_PATH } from 'routes/paths';
import { Features } from 'utils/optimizely/types';

import { HEADING } from './constants';
import BasicsForm from './form';
import { BasicsFormValues } from './form/types';
import { useGetBasicsQuery } from './graphql/GetSurveyBasics';
import { useUpdateBasicsMutation } from './graphql/UpdateBasics';
import { useUpdateOccupationMutation } from './graphql/UpdateOccupation';
import SelectUser from './SelectUser';
import buildInitialValues from './utils/buildInitialValues';
import buildInput from './utils/buildInput';
import { createValidate } from './utils/validate';

const Basics = () => {
  const [PageConsolidationFF] = useDecision(Features.PageConsolidation);
  const isPageConsolidationEnabled = PageConsolidationFF.enabled;
  const trackStepCompleted = useNavigatorTracking(Steps.SurveyBasics);
  const nextPage = PageConsolidationFF.enabled ? HEALTH_BASICS_PATH : OCCUPATION_PATH;

  const validate = useMemo(
    () => createValidate(isPageConsolidationEnabled),
    [isPageConsolidationEnabled]
  );
  const [showSelectUser, setShowSelectUser] = useState(true);
  const { data, loading } = useGetBasicsQuery();
  const initialValues = buildInitialValues(data);
  const [updateBasics] = useUpdateBasicsMutation();

  const history = useHistory();

  useEffect(() => {
    backendAnalyticsTrack({
      action: 'track_disability_navigator_start',
    });
  }, []);

  const [updateOccupation] = useUpdateOccupationMutation();

  const onCompleted = () => {
    trackStepCompleted();
    history.push(nextPage);
  };

  const onSubmit = (values: BasicsFormValues) => {
    updateBasics({
      variables: {
        input: {
          dateOfBirth: values.dateOfBirth,
          gender: values.gender,
          stateId: values.stateId,
        },
      },
    });
    if (isPageConsolidationEnabled === true) {
      updateOccupation({
        variables: {
          input: buildInput(values),
        },
      });
    }
    onCompleted();
  };

  if (loading) {
    return <GlobalLoader />;
  }

  if (showSelectUser && !!data?.currentUser.email) {
    return (
      <SelectUser
        firstName={data?.currentUser.firstName ?? ''}
        setShowSelectUser={setShowSelectUser}
      />
    );
  }

  return (
    <NavigatorContent heading={HEADING} title={Steps.SurveyBasics}>
      <Form<BasicsFormValues, InitialValues<BasicsFormValues>>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {(formProps) => (
          <BasicsForm {...formProps} isPageConsolidationEnabled={isPageConsolidationEnabled} />
        )}
      </Form>
    </NavigatorContent>
  );
};

export default Basics;
